import { useFontStore } from '../stores/fontStore';
import { useFontAliasStore, type FontAlias } from '../stores/fontAliasStore';
import { normalizeFontName as normalize } from './fontNameNormalize';
import { resolveFontCandidate, type MatchTier } from './fontCandidateResolver';

export interface FontMatchResult {
  originalCss: string;
  matched: boolean;
  matchedFamily: string | null;
  matchedWeight: number | null;
  matchedStyle: string | null;
  /** cs_fonts row id for the chosen variant. Null when no variant exists or manual selection is needed. */
  matchedVariantId: string | null;
  matchTier: MatchTier | null;
  confidence: 'high' | 'medium' | 'low';
  availableVariants: Array<{ weight: number; style: string; id: string }>;
  needsManualVariantSelection: boolean;
  sampleText: string;
}

export interface SvgFontDeclaration {
  cssValue: string;
  cssWeight?: string;
  cssStyle?: string;
  elementIds: string[];
}

const POSTSCRIPT_WEIGHT_MAP: Record<string, number> = {
  'thin': 100,
  'hairline': 100,
  'extralight': 200,
  'ultralight': 200,
  'light': 300,
  'regular': 400,
  'normal': 400,
  'medium': 500,
  'semibold': 600,
  'demibold': 600,
  'demi': 600,
  'bold': 700,
  'extrabold': 800,
  'ultrabold': 800,
  'black': 900,
  'heavy': 900,
  'th': 100,
  'xlt': 200,
  'lt': 300,
  'rg': 400,
  'md': 500,
  'smbd': 600,
  'dm': 600,
  'bd': 700,
  'xbd': 800,
  'blk': 900,
  'bk': 900,
};


export function parseFallbackChain(cssValue: string): string[] {
  return cssValue
    .split(',')
    .map(v => v.trim().replace(/^['"]|['"]$/g, ''))
    .filter(v => v.length > 0);
}

function extractWeightFromPostScript(name: string): { family: string; weightToken: string | null } {
  const lastHyphen = name.lastIndexOf('-');
  if (lastHyphen > 0) {
    return {
      family: name.substring(0, lastHyphen),
      weightToken: name.substring(lastHyphen + 1),
    };
  }
  return { family: name, weightToken: null };
}

const EMBEDDED_WEIGHT_PATTERNS: [RegExp, string][] = [
  [/(?:Extra|Ultra)Bold(?:Italic)?$/i, 'ExtraBold'],
  [/(?:Semi|Demi)Bold(?:Italic)?$/i, 'SemiBold'],
  [/(?:Extra|Ultra)Light(?:Italic)?$/i, 'ExtraLight'],
  [/Bold(?:Italic)?$/i, 'Bold'],
  [/Medium(?:Italic)?$/i, 'Medium'],
  [/Light(?:Italic)?$/i, 'Light'],
  [/Thin(?:Italic)?$/i, 'Thin'],
  [/Black(?:Italic)?$/i, 'Black'],
  [/Heavy(?:Italic)?$/i, 'Heavy'],
  [/Regular$/i, 'Regular'],
];

function extractEmbeddedWeight(candidateName: string): string | null {
  for (const [pattern, token] of EMBEDDED_WEIGHT_PATTERNS) {
    if (pattern.test(candidateName)) {
      const match = candidateName.match(pattern);
      return match ? match[0] : token;
    }
  }
  return null;
}

function resolveWeightToken(token: string): { weight: number | null; style: string } {
  if (!token) return { weight: 400, style: 'normal' };

  let style = 'normal';
  let remainder = token;

  if (/italic/i.test(remainder)) {
    style = 'italic';
    remainder = remainder.replace(/italic/i, '');
  } else if (/oblique/i.test(remainder)) {
    style = 'oblique';
    remainder = remainder.replace(/oblique/i, '');
  }
  if (/\bIt\b/.test(remainder)) {
    style = 'italic';
    remainder = remainder.replace(/\bIt\b/, '');
  }

  remainder = remainder.trim();

  if (!remainder) return { weight: 400, style };

  const lower = remainder.toLowerCase();
  if (POSTSCRIPT_WEIGHT_MAP[lower] !== undefined) {
    return { weight: POSTSCRIPT_WEIGHT_MAP[lower], style };
  }

  const normalized = normalize(remainder);
  if (POSTSCRIPT_WEIGHT_MAP[normalized] !== undefined) {
    return { weight: POSTSCRIPT_WEIGHT_MAP[normalized], style };
  }

  const condensedStripped = remainder.replace(/(Cn|Cond|Condensed|Ext|Extended|Narrow|Wide|Comp|Compressed)/gi, '').trim();
  if (condensedStripped) {
    const csLower = condensedStripped.toLowerCase();
    if (POSTSCRIPT_WEIGHT_MAP[csLower] !== undefined) {
      return { weight: POSTSCRIPT_WEIGHT_MAP[csLower], style };
    }
    const csNormalized = normalize(condensedStripped);
    if (POSTSCRIPT_WEIGHT_MAP[csNormalized] !== undefined) {
      return { weight: POSTSCRIPT_WEIGHT_MAP[csNormalized], style };
    }
  }

  return { weight: null, style };
}

const TIER_CONFIDENCE: Record<MatchTier, 'high' | 'medium' | 'low'> = {
  exact: 'high',
  alias: 'high',
  normalized: 'high',
  contains: 'medium',
  token: 'medium',
  filename: 'medium',
  manual: 'high',
};

export function matchSvgFont(
  cssValue: string,
  cssWeight?: string,
  cssStyle?: string,
  aliases?: FontAlias[]
): FontMatchResult {
  const fonts = useFontStore.getState().fonts;

  if (!fonts || fonts.length === 0) {
    return {
      originalCss: cssValue,
      matched: false,
      matchedFamily: null,
      matchedWeight: null,
      matchedStyle: null,
      matchedVariantId: null,
      matchTier: null,
      confidence: 'low',
      availableVariants: [],
      needsManualVariantSelection: false,
      sampleText: '',
    };
  }

  const aliasList = aliases ?? useFontAliasStore.getState().aliases;

  const candidate = resolveFontCandidate(cssValue, { fonts, aliases: aliasList });
  if (!candidate) {
    return {
      originalCss: cssValue,
      matched: false,
      matchedFamily: null,
      matchedWeight: null,
      matchedStyle: null,
      matchedVariantId: null,
      matchTier: null,
      confidence: 'low',
      availableVariants: [],
      needsManualVariantSelection: false,
      sampleText: '',
    };
  }

  const familyMap = new Map<string, typeof fonts>();
  for (const font of fonts) {
    if (!font.isActive) continue;
    const existing = familyMap.get(font.fontFamily) || [];
    existing.push(font);
    familyMap.set(font.fontFamily, existing);
  }

  if (candidate.tier === 'alias') {
    const variants = familyMap.get(candidate.matchedFamily) || [];
    const availableVariants = variants.map((v) => ({
      weight: parseInt(String(v.fontWeight), 10) || 400,
      style: v.fontStyle || 'normal',
      id: v.id,
    }));
    const w = candidate.matchedWeight ?? 400;
    const s = candidate.matchedStyle ?? 'normal';
    const exact = availableVariants.find((v) => v.weight === w && v.style === s);
    const picked = exact || availableVariants[0];
    return {
      originalCss: cssValue,
      matched: true,
      matchedFamily: candidate.matchedFamily,
      matchedWeight: picked ? picked.weight : w,
      matchedStyle: picked ? picked.style : s,
      matchedVariantId: picked ? picked.id : null,
      matchTier: 'alias',
      confidence: 'high',
      availableVariants,
      needsManualVariantSelection: false,
      sampleText: '',
    };
  }

  const { weightToken } = extractWeightFromPostScript(
    parseFallbackChain(cssValue)[0] || cssValue
  );
  return buildResult(
    cssValue,
    candidate.matchedFamily,
    familyMap,
    weightToken,
    cssWeight,
    cssStyle,
    candidate.tier,
    TIER_CONFIDENCE[candidate.tier]
  );
}

function buildResult(
  originalCss: string,
  matchedFamily: string,
  familyMap: Map<string, any[]>,
  weightToken: string | null,
  cssWeight?: string,
  cssStyle?: string,
  matchTier: FontMatchResult['matchTier'] = null,
  baseConfidence: 'high' | 'medium' | 'low' = 'medium'
): FontMatchResult {
  const variants = familyMap.get(matchedFamily) || [];
  const availableVariants = variants.map((v: any) => ({
    weight: parseInt(v.fontWeight, 10) || 400,
    style: v.fontStyle || 'normal',
    id: v.id,
  }));

  let resolvedWeight: number | null = null;
  let resolvedStyle: string = cssStyle || 'normal';

  if (weightToken) {
    const { weight, style } = resolveWeightToken(weightToken);
    resolvedWeight = weight;
    if (style !== 'normal') resolvedStyle = style;
  }

  if (resolvedWeight === null && !weightToken) {
    const candidates = parseFallbackChain(originalCss);
    for (const candidate of candidates) {
      const embedded = extractEmbeddedWeight(candidate);
      if (embedded) {
        const { weight, style } = resolveWeightToken(embedded);
        resolvedWeight = weight;
        if (style !== 'normal') resolvedStyle = style;
        break;
      }
    }
  }

  if (resolvedWeight === null && cssWeight) {
    const numWeight = parseInt(cssWeight, 10);
    if (!isNaN(numWeight)) {
      resolvedWeight = numWeight;
    } else {
      const mapped = POSTSCRIPT_WEIGHT_MAP[cssWeight.toLowerCase()];
      if (mapped !== undefined) resolvedWeight = mapped;
    }
  }

  if (resolvedWeight === null && availableVariants.length === 1) {
    resolvedWeight = availableVariants[0].weight;
    resolvedStyle = availableVariants[0].style;
  }

  const needsManual = resolvedWeight === null ||
    !availableVariants.some(
      (v: { weight: number; style: string }) =>
        v.weight === resolvedWeight && v.style === resolvedStyle
    );

  if (resolvedWeight !== null && needsManual && availableVariants.length > 0) {
    const sorted = [...availableVariants].sort(
      (a: { weight: number }, b: { weight: number }) =>
        Math.abs(a.weight - resolvedWeight!) - Math.abs(b.weight - resolvedWeight!)
    );
    const closest = sorted[0];
    if (Math.abs(closest.weight - resolvedWeight) <= 100) {
      return {
        originalCss,
        matched: true,
        matchedFamily,
        matchedWeight: closest.weight,
        matchedStyle: closest.style,
        matchedVariantId: closest.id,
        matchTier,
        confidence: 'medium',
        availableVariants,
        needsManualVariantSelection: false,
        sampleText: '',
      };
    }
  }

  const exactVariant =
    resolvedWeight !== null && !needsManual
      ? availableVariants.find(
          (v) => v.weight === resolvedWeight && v.style === resolvedStyle
        )
      : undefined;

  return {
    originalCss,
    matched: true,
    matchedFamily,
    matchedWeight: needsManual ? null : resolvedWeight,
    matchedStyle: needsManual ? null : resolvedStyle,
    matchedVariantId: exactVariant ? exactVariant.id : null,
    matchTier,
    confidence: needsManual ? 'low' : baseConfidence,
    availableVariants,
    needsManualVariantSelection: needsManual,
    sampleText: '',
  };
}

export function extractSvgFontDeclarations(svgString: string): SvgFontDeclaration[] {
  const declarations = new Map<string, SvgFontDeclaration>();

  const styleBlockRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let styleMatch;
  while ((styleMatch = styleBlockRegex.exec(svgString)) !== null) {
    const cssContent = styleMatch[1];
    const ruleRegex = /([.#][\w-]+)\s*\{([^}]+)\}/g;
    let ruleMatch;
    while ((ruleMatch = ruleRegex.exec(cssContent)) !== null) {
      const ruleBody = ruleMatch[2];
      const fontFamilyMatch = ruleBody.match(/font-family\s*:\s*([^;]+)/i);
      if (fontFamilyMatch) {
        const cssValue = fontFamilyMatch[1].trim();
        const weightMatch = ruleBody.match(/font-weight\s*:\s*([^;]+)/i);
        const styleMatch2 = ruleBody.match(/font-style\s*:\s*([^;]+)/i);

        if (!declarations.has(cssValue)) {
          declarations.set(cssValue, {
            cssValue,
            cssWeight: weightMatch ? weightMatch[1].trim() : undefined,
            cssStyle: styleMatch2 ? styleMatch2[1].trim() : undefined,
            elementIds: [],
          });
        }
      }
    }
  }

  const inlineStyleRegex = /style\s*=\s*"([^"]*font-family\s*:[^"]*?)"/gi;
  let inlineMatch;
  while ((inlineMatch = inlineStyleRegex.exec(svgString)) !== null) {
    const styleStr = inlineMatch[1];
    const fontFamilyMatch = styleStr.match(/font-family\s*:\s*([^;]+)/i);
    if (fontFamilyMatch) {
      const cssValue = fontFamilyMatch[1].trim();
      const weightMatch = styleStr.match(/font-weight\s*:\s*([^;]+)/i);
      const styleMatch2 = styleStr.match(/font-style\s*:\s*([^;]+)/i);

      if (!declarations.has(cssValue)) {
        declarations.set(cssValue, {
          cssValue,
          cssWeight: weightMatch ? weightMatch[1].trim() : undefined,
          cssStyle: styleMatch2 ? styleMatch2[1].trim() : undefined,
          elementIds: [],
        });
      }
    }
  }

  const attrRegex = /font-family\s*=\s*"([^"]+)"/gi;
  let attrMatch;
  while ((attrMatch = attrRegex.exec(svgString)) !== null) {
    const before = svgString.substring(Math.max(0, attrMatch.index - 10), attrMatch.index);
    if (before.includes('style=')) continue;

    const cssValue = attrMatch[1].trim();
    if (!declarations.has(cssValue)) {
      declarations.set(cssValue, {
        cssValue,
        elementIds: [],
      });
    }
  }

  return Array.from(declarations.values());
}

function collectSampleTextByFontFamily(svgString: string): Map<string, string> {
  const out = new Map<string, string>();
  try {
    const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return out;

    const classFamily = new Map<string, string>();
    const styleBlocks = doc.querySelectorAll('style');
    styleBlocks.forEach(block => {
      const css = block.textContent || '';
      const ruleRegex = /\.([\w-]+)\s*\{([^}]+)\}/g;
      let m: RegExpExecArray | null;
      while ((m = ruleRegex.exec(css)) !== null) {
        const className = m[1];
        const body = m[2];
        const ffMatch = body.match(/font-family\s*:\s*([^;]+)/i);
        if (ffMatch) classFamily.set(className, ffMatch[1].trim());
      }
    });

    const resolveFamilyForNode = (node: Element): string | null => {
      let cur: Element | null = node;
      while (cur) {
        const styleAttr = cur.getAttribute('style');
        if (styleAttr) {
          const m = styleAttr.match(/font-family\s*:\s*([^;]+)/i);
          if (m) return m[1].trim();
        }
        const ffAttr = cur.getAttribute('font-family');
        if (ffAttr) return ffAttr.trim();
        const classAttr = cur.getAttribute('class');
        if (classAttr) {
          const classes = classAttr.split(/\s+/);
          for (const c of classes) {
            if (classFamily.has(c)) return classFamily.get(c) || null;
          }
        }
        cur = cur.parentElement;
      }
      return null;
    };

    const record = (family: string | null, text: string) => {
      if (!family) return;
      const trimmed = text.replace(/\s+/g, ' ').trim();
      if (!trimmed) return;
      const existing = out.get(family) || '';
      if (trimmed.length > existing.length) out.set(family, trimmed);
    };

    const textNodes = doc.querySelectorAll('text');
    textNodes.forEach(textEl => {
      const tspans = textEl.querySelectorAll('tspan');
      if (tspans.length > 0) {
        tspans.forEach(ts => {
          record(resolveFamilyForNode(ts), ts.textContent || '');
        });
      } else {
        record(resolveFamilyForNode(textEl), textEl.textContent || '');
      }
    });
  } catch {
    // best-effort; fall through with empty map
  }
  return out;
}

const FABRIC_DEFAULT_FAMILY = 'Times New Roman';

function resolveInheritedFontFamily(node: Element, classFamily: Map<string, string>): string | null {
  let cur: Element | null = node;
  while (cur) {
    const styleAttr = cur.getAttribute('style');
    if (styleAttr) {
      const m = styleAttr.match(/font-family\s*:\s*([^;]+)/i);
      if (m) return m[1].trim();
    }
    const ffAttr = cur.getAttribute('font-family');
    if (ffAttr) return ffAttr.trim();
    const classAttr = cur.getAttribute('class');
    if (classAttr) {
      const classes = classAttr.split(/\s+/);
      for (const c of classes) {
        if (classFamily.has(c)) return classFamily.get(c) || null;
      }
    }
    cur = cur.parentElement;
  }
  return null;
}

export function detectUnresolvedTextFontFamily(svgString: string): boolean {
  try {
    const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return false;

    const classFamily = new Map<string, string>();
    const tagFontFamily = new Map<string, string>();

    const styleBlocks = doc.querySelectorAll('style');
    styleBlocks.forEach(block => {
      const css = (block.textContent || '').replace(/\/\*[\s\S]*?\*\//g, '');

      const classRegex = /\.([\w-]+)\s*\{([^}]+)\}/g;
      let cm: RegExpExecArray | null;
      while ((cm = classRegex.exec(css)) !== null) {
        const body = cm[2];
        const ff = body.match(/font-family\s*:\s*([^;]+)/i);
        if (ff) classFamily.set(cm[1], ff[1].trim());
      }

      const blockRegex = /([^{}]+)\{([^}]*)\}/g;
      let bm: RegExpExecArray | null;
      while ((bm = blockRegex.exec(css)) !== null) {
        const selectorList = bm[1].trim();
        const body = bm[2];
        const ff = body.match(/font-family\s*:\s*([^;]+)/i);
        if (!ff) continue;
        const family = ff[1].trim();
        for (const sel of selectorList.split(',').map(s => s.trim())) {
          const parts = sel.split(/\s+/);
          const rightmost = parts[parts.length - 1];
          if (!rightmost) continue;
          const tm = rightmost.match(/^([a-zA-Z][a-zA-Z0-9]*)$/);
          if (!tm) continue;
          const tag = tm[1].toLowerCase();
          if (tag === 'text' || tag === 'tspan' || tag === 'g' || tag === 'svg') {
            if (!tagFontFamily.has(tag)) tagFontFamily.set(tag, family);
          }
        }
      }
    });

    const textLike: Element[] = [];
    const texts = doc.getElementsByTagName('text');
    for (let i = 0; i < texts.length; i++) textLike.push(texts[i]);
    const tspans = doc.getElementsByTagName('tspan');
    for (let i = 0; i < tspans.length; i++) textLike.push(tspans[i]);

    for (const node of textLike) {
      const textContent = (node.textContent || '').trim();
      if (!textContent) continue;

      const direct = resolveInheritedFontFamily(node, classFamily);
      if (direct) continue;

      let tagMatched: string | null = null;
      let cur: Element | null = node;
      while (cur) {
        const tn = cur.tagName?.toLowerCase();
        if (tn && tagFontFamily.has(tn)) {
          tagMatched = tagFontFamily.get(tn) || null;
          break;
        }
        cur = cur.parentElement;
      }
      if (tagMatched) continue;

      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function matchAllSvgFonts(svgString: string, aliases?: FontAlias[]): FontMatchResult[] {
  const declarations = extractSvgFontDeclarations(svgString);
  const sampleMap = collectSampleTextByFontFamily(svgString);
  const aliasList = aliases ?? useFontAliasStore.getState().aliases;

  const hasUnresolved = detectUnresolvedTextFontFamily(svgString);
  let forcedSyntheticDeclared = false;
  if (hasUnresolved) {
    const alreadyDeclared = declarations.some(
      d => d.cssValue.toLowerCase().replace(/['"]/g, '').trim() === FABRIC_DEFAULT_FAMILY.toLowerCase()
    );
    if (!alreadyDeclared) {
      console.log('[SVG Font] Detected text node(s) with no resolvable font-family; adding synthetic declaration to force font match modal');
      declarations.push({ cssValue: FABRIC_DEFAULT_FAMILY, elementIds: [] });
      forcedSyntheticDeclared = true;
    }
  }

  return declarations.map(decl => {
    const result = matchSvgFont(decl.cssValue, decl.cssWeight, decl.cssStyle, aliasList);
    const sample = sampleMap.get(decl.cssValue) || '';
    if (forcedSyntheticDeclared && decl.cssValue === FABRIC_DEFAULT_FAMILY) {
      return {
        ...result,
        matched: false,
        matchedFamily: null,
        matchedWeight: null,
        matchedStyle: null,
        matchedVariantId: null,
        needsManualVariantSelection: true,
        confidence: 'low',
        sampleText: sample,
      };
    }
    return { ...result, sampleText: sample };
  });
}
